import { useEffect, useState } from 'react';

/**
 * What a consent callback told us, in words — shown in whichever settings section the
 * person returns to.
 *
 * The server redirects back to `${returnPath}?gmail=<code>` (or `?microsoft=`). It sends
 * a code rather than a sentence, because the wording a person reads belongs in the UI.
 * Anything unrecognised falls through to a plain message rather than the raw code.
 */
export type Outcome = {
  tone: 'success' | 'error'; text: string;
  /** Set when the flow was a capability upgrade; the banner checks what was granted. */
  upgrade?: MailboxKind;
};
export type MailboxKind = 'gmail' | 'microsoft';

export const GMAIL_OUTCOMES: Record<string, Outcome> = {
  DIFFERENT_ACCOUNT: {
    tone: 'error',
    text: 'That is a different Google account from the mailbox connected here. Reconnect with the same account, '
      + 'or disconnect this mailbox first. Nothing was changed.',
  },
  MAILBOX_ALREADY_CONNECTED: {
    tone: 'error', text: 'This workspace already has a mailbox connected. Disconnect it before connecting another.',
  },
  connected: { tone: 'success', text: 'Gmail is connected. Nothing has been read yet.' },
  GMAIL_CONSENT_DECLINED: {
    tone: 'error', text: 'You chose not to grant access, so nothing was changed.',
  },
  GMAIL_SCOPES_NOT_GRANTED: {
    tone: 'error',
    text: 'Read access to Gmail was not granted. The permission to read messages is '
      + 'required — try again and leave it ticked.',
  },
  GMAIL_AUTHORIZATION_EXPIRED: {
    tone: 'error', text: 'That sign-in link had expired or was already used. Start again.',
  },
  REAUTH_REQUIRED: {
    tone: 'error',
    text: 'Google did not return a lasting authorisation. Try again, and choose your '
      + 'account rather than an already-signed-in one.',
  },
  GMAIL_NOT_CONFIGURED: {
    tone: 'error', text: 'Gmail connections are not available in this workspace.',
  },
};

/*
 * Separate from Gmail's rather than merged, because the failures genuinely differ:
 * Microsoft folds "your administrator blocked this" into the same `access_denied` that a
 * user's own refusal produces, and telling those apart is the difference between "try
 * again" and "ask your IT administrator".
 */
export const MICROSOFT_OUTCOMES: Record<string, Outcome> = {
  DIFFERENT_ACCOUNT: {
    tone: 'error',
    text: 'That is a different Microsoft account from the mailbox connected here. Reconnect with the same account, '
      + 'or disconnect this mailbox first. Nothing was changed.',
  },
  MAILBOX_ALREADY_CONNECTED: {
    tone: 'error', text: 'This workspace already has a mailbox connected. Disconnect it before connecting another.',
  },
  connected: { tone: 'success', text: 'Outlook is connected. Nothing has been read yet.' },
  MS_CONSENT_DECLINED: {
    tone: 'error', text: 'You chose not to grant access, so nothing was changed.',
  },
  MS_ADMIN_CONSENT_REQUIRED: {
    tone: 'error',
    text: 'Your Microsoft 365 administrator has to approve this application before you '
      + 'can connect. Nothing was changed.',
  },
  MS_AUTHORIZATION_EXPIRED: {
    tone: 'error', text: 'That sign-in link had expired or was already used. Start again.',
  },
  MS_STATE_MISMATCH: {
    tone: 'error', text: 'That sign-in could not be matched to a request from this browser. Start again.',
  },
  MS_NONCE_MISMATCH: {
    tone: 'error', text: 'The sign-in response did not match this request. Start again.',
  },
  MS_ACCOUNT_MISMATCH: {
    tone: 'error', text: 'The account that signed in is not the mailbox connected here.',
  },
  REAUTH_REQUIRED: {
    tone: 'error',
    text: 'Microsoft did not return a lasting authorisation. Try again, and choose your '
      + 'account rather than an already-signed-in one.',
  },
  MS_NOT_CONFIGURED: {
    tone: 'error', text: 'Outlook connections are not available in this workspace.',
  },
};

const UPGRADE_KEY = 'inboxsignal.pendingUpgrade';

/**
 * Notes that the consent flow about to start is a capability upgrade, not a connect.
 *
 * The callback says only `connected` either way, and "Gmail is connected. Nothing has
 * been read yet." would be the wrong sentence for somebody who just allowed label
 * creation on a mailbox that has been connected for weeks. Session storage, wrapped,
 * because it may be unavailable — and then the plain sentence is still true enough.
 */
export function rememberUpgrade(kind: MailboxKind): void {
  try { window.sessionStorage.setItem(UPGRADE_KEY, kind); } catch { /* unavailable */ }
}

function consumeUpgrade(kind: MailboxKind): boolean {
  try {
    const v = window.sessionStorage.getItem(UPGRADE_KEY);
    window.sessionStorage.removeItem(UPGRADE_KEY);
    return v === kind;
  } catch {
    return false;
  }
}

function readOutcome(): Outcome | null {
  const params = new URLSearchParams(window.location.search);
  for (const kind of ['gmail', 'microsoft'] as const) {
    const code = params.get(kind);
    if (!code) continue;
    const word = kind === 'gmail' ? 'Gmail' : 'Outlook';
    const table = kind === 'gmail' ? GMAIL_OUTCOMES : MICROSOFT_OUTCOMES;
    const upgraded = consumeUpgrade(kind);
    if (code === 'connected' && upgraded) {
      /*
       * "Connected" only says consent completed. Whether the extra permission was ticked
       * is a fact about the grant, so the banner reads it from the connection itself.
       */
      return {
        tone: 'success', upgrade: kind,
        text: kind === 'gmail'
          ? 'Label creation is allowed. Your Gmail connection is otherwise unchanged.'
          : 'Category creation is allowed. Your Outlook connection is otherwise unchanged.',
      };
    }
    return table[code] ?? { tone: 'error', text: `${word} could not be connected. Try again.` };
  }
  return null;
}

/**
 * Reads the callback parameter once, then strips it, so a refresh does not re-announce
 * something that happened minutes ago.
 */
export function useOAuthOutcome(): [Outcome | null, () => void] {
  const [outcome, setOutcome] = useState<Outcome | null>(() => readOutcome());
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('gmail') && !params.has('microsoft')) return;
    params.delete('gmail');
    params.delete('microsoft');
    const rest = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (rest ? `?${rest}` : ''));
  }, []);
  return [outcome, () => setOutcome(null)];
}
